import { useState, useMemo } from "react";

export type SortKey = "name" | "revenue" | "seatCount";
export type SortDirection = "ascending" | "descending";

export interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

export const useSortableTable = <T extends Record<string, any>>(
  data: T[],
  initialKey: SortKey = "name"
) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: initialKey,
    direction: "ascending",
  });

  const sortedData = useMemo(() => {
    const sortableData = [...data];

    sortableData.sort((a, b) => {
      const valueA = a[sortConfig.key];
      const valueB = b[sortConfig.key];

      if (valueA < valueB) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

    return sortableData;
  }, [data, sortConfig]);

  const requestSort = (key: SortKey) => {
    let direction: SortDirection = "ascending";

    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }

    setSortConfig({ key, direction });
  };

  const getSortIndicator = (columnKey: SortKey): string | null => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === "ascending" ? " ▲" : " ▼";
    }
    return null;
  };

  return { sortedData, requestSort, getSortIndicator, sortConfig };
};
